import { useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { ApiError } from '../shared/api/client';
import { api, type DriverView } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { useDepartments } from '../shared/lib/departments';
import {
  DRIVER_LANGUAGES,
  EMPLOYMENT_KINDS,
  EXTERNAL_CODE_MAX,
  FULL_NAME_MAX,
  emptyDriverForm,
  errorsFromProblem,
  hasErrors,
  toDriverInput,
  validateDriverForm,
  type DriverField,
  type DriverFormErrors,
  type DriverFormState,
} from '../shared/lib/driver-form';
import { formatPhoneInput } from '../shared/lib/phone';
import { Drawer, primaryButtonStyle, secondaryButtonStyle } from '../shared/ui/primitives';

/**
 * Заведение курьера — правая панель поверх списка (разд. 12: «список слева-таблицей,
 * редактирование в drawer справа»).
 *
 * Только создание: `PATCH /web/drivers/{driver_id}` в контракте нет, а писать правку
 * мимо контракта нельзя (разд. 16). Появится операция — сюда добавится режим правки
 * с теми же полями.
 *
 * Отделы — только свои: сервер всё равно отклонит курьера в чужом отделе
 * (`department_forbidden`), и узнавать об этом отказом хуже, чем не видеть варианта.
 *
 * Сохранение пессимистичное (разд. 15): кнопка блокируется до ответа, ошибка — рядом
 * с полем, а не тостом. Повторное нажатие после сбоя сети не заведёт второго курьера:
 * номер телефона уникален, и сервер ответит `phone_taken`.
 */
export function DriverDrawer({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: (driver: DriverView) => void;
}): ReactElement {
  const messages = useMessages();
  const text = messages.cabinet.drivers;
  const departments = useDepartments();

  const [form, setForm] = useState<DriverFormState>(() => emptyDriverForm(departments));
  const [errors, setErrors] = useState<DriverFormErrors>({});
  // Поля проверяются после первой попытки сохранить: подсвечивать красным пустую форму,
  // которую человек ещё не начал заполнять, — шум.
  const [submitted, setSubmitted] = useState(false);
  const [general, setGeneral] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const update = (patch: Partial<DriverFormState>): void => {
    const next = { ...form, ...patch };

    setForm(next);

    if (submitted) {
      setErrors(validateDriverForm(next));
    }
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitted(true);
    setGeneral(null);

    const found = validateDriverForm(form);
    setErrors(found);

    if (hasErrors(found)) {
      return;
    }

    setPending(true);

    try {
      onCreated(await api.createDriver(toDriverInput(form)));
    } catch (cause) {
      if (!(cause instanceof ApiError)) {
        setGeneral(text.errors.failed);

        return;
      }

      const mapped = errorsFromProblem(cause);
      setErrors(mapped.fields);

      if (mapped.general) {
        setGeneral(`${generalText(cause)} · ${text.errors.code}: ${supportCode(cause)}`);
      }
    } finally {
      setPending(false);
    }
  };

  const generalText = (cause: ApiError): string => {
    if (cause.code === 'validation_failed') {
      return text.errors.validation;
    }

    if (cause.status === FORBIDDEN) {
      return text.errors.forbidden;
    }

    return text.errors.failed;
  };

  const errorOf = (field: DriverField): string | null => {
    const code = errors[field];

    return code === undefined ? null : text.errors[code];
  };

  return (
    <Drawer title={<h2>{text.createTitle}</h2>} onClose={onClose}>
      <form noValidate onSubmit={(event) => void submit(event)} style={formStyle}>
        <FormField
          id="driver-full-name"
          label={text.fullName}
          required={text.required}
          error={errorOf('full_name')}
        >
          <input
            id="driver-full-name"
            value={form.full_name}
            maxLength={FULL_NAME_MAX}
            autoComplete="off"
            aria-invalid={errors.full_name !== undefined}
            aria-describedby={errors.full_name === undefined ? undefined : 'driver-full-name-error'}
            onChange={(event) => update({ full_name: event.target.value })}
          />
        </FormField>

        <FormField
          id="driver-phone"
          label={text.phone}
          required={text.required}
          hint={text.phoneHint}
          error={errorOf('phone')}
        >
          <input
            id="driver-phone"
            className="num"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={form.phone}
            aria-invalid={errors.phone !== undefined}
            aria-describedby={errors.phone === undefined ? 'driver-phone-hint' : 'driver-phone-error'}
            onChange={(event) => update({ phone: formatPhoneInput(event.target.value) })}
          />
        </FormField>

        <div style={pairStyle}>
          <FormField id="driver-employment" label={text.employment} error={null}>
            <select
              id="driver-employment"
              value={form.employment}
              onChange={(event) =>
                update({ employment: event.target.value as DriverFormState['employment'] })
              }
            >
              <option value="">{text.employmentNone}</option>
              {EMPLOYMENT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {text.employmentKinds[kind]}
                </option>
              ))}
            </select>
          </FormField>

          <FormField id="driver-language" label={text.language} error={null}>
            <select
              id="driver-language"
              value={form.language}
              onChange={(event) =>
                update({ language: event.target.value as DriverFormState['language'] })
              }
            >
              {DRIVER_LANGUAGES.map((language) => (
                <option key={language} value={language}>
                  {text.languages[language]}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField
          id="driver-external-code"
          label={text.externalCode}
          hint={text.externalCodeHint}
          error={errorOf('external_code')}
        >
          <input
            id="driver-external-code"
            className="num"
            value={form.external_code}
            maxLength={EXTERNAL_CODE_MAX}
            autoComplete="off"
            aria-invalid={errors.external_code !== undefined}
            aria-describedby={
              errors.external_code === undefined
                ? 'driver-external-code-hint'
                : 'driver-external-code-error'
            }
            onChange={(event) => update({ external_code: event.target.value })}
          />
        </FormField>

        <fieldset
          style={fieldsetStyle}
          aria-describedby={errors.modes === undefined ? 'driver-modes-hint' : 'driver-modes-error'}
        >
          <legend style={labelStyle}>{`${text.departments} · ${text.required}`}</legend>
          <div style={checksStyle}>
            {departments.length === 0 ? (
              <span style={errorStyle}>{messages.cabinet.users.noDepartment}</span>
            ) : null}
            {departments.map((mode) => (
              <label key={mode} style={checkStyle}>
                <input
                  type="checkbox"
                  checked={form.modes.includes(mode)}
                  onChange={() =>
                    update({
                      modes: form.modes.includes(mode)
                        ? form.modes.filter((item) => item !== mode)
                        : [...form.modes, mode],
                    })
                  }
                />
                <span>{messages.cabinet.modes[mode]}</span>
              </label>
            ))}
          </div>
          <FieldNote id="driver-modes" hint={text.departmentsHint} error={errorOf('modes')} />
        </fieldset>

        <label style={checkStyle}>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(event) => update({ is_active: event.target.checked })}
          />
          <span>{text.active}</span>
        </label>

        {general === null ? null : (
          <div role="alert" style={alertStyle}>
            {general}
          </div>
        )}

        <div style={actionsStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            {messages.cabinet.common.cancel}
          </button>
          <button
            type="submit"
            disabled={pending}
            aria-busy={pending}
            style={{ ...primaryButtonStyle, opacity: pending ? DISABLED_OPACITY : 1 }}
          >
            {pending ? text.saving : text.submit}
          </button>
        </div>
      </form>
    </Drawer>
  );
}

function FormField({
  id,
  label,
  required,
  hint,
  error,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly required?: string | undefined;
  readonly hint?: string | undefined;
  readonly error: string | null;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <div style={fieldStyle}>
      <label htmlFor={id} style={labelStyle}>
        {required === undefined ? label : `${label} · ${required}`}
      </label>
      {children}
      <FieldNote id={id} hint={hint} error={error} />
    </div>
  );
}

/** Подсказка или ошибка под полем: ошибка вытесняет подсказку, место под строку одно. */
function FieldNote({
  id,
  hint,
  error,
}: {
  readonly id: string;
  readonly hint?: string | undefined;
  readonly error: string | null;
}): ReactElement | null {
  if (error !== null) {
    return (
      <span id={`${id}-error`} style={errorStyle}>
        {error}
      </span>
    );
  }

  return hint === undefined ? null : (
    <span id={`${id}-hint`} style={hintStyle}>
      {hint}
    </span>
  );
}

const FORBIDDEN = 403;
const DISABLED_OPACITY = 0.6;

/** Код для поддержки: идентификатор запроса, если сервер его дал, иначе код ошибки. */
function supportCode(cause: ApiError): string {
  return cause.problem.trace_id ?? `${cause.status} ${cause.code}`;
}

const formStyle = { display: 'grid', gap: 'var(--sp-4)', minWidth: 0 } as const;
const fieldStyle = { display: 'grid', gap: 'var(--sp-1)', minWidth: 0 } as const;
const pairStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 'var(--sp-3)',
} as const;
const labelStyle = { color: 'var(--text-muted)', fontSize: 'var(--fs-caption)', padding: 0 } as const;
const hintStyle = { color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' } as const;
const errorStyle = { color: 'var(--danger)', fontSize: 'var(--fs-caption)' } as const;
const fieldsetStyle = {
  border: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: 'var(--sp-1)',
  minWidth: 0,
} as const;
const checksStyle = { display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-4)' } as const;
const checkStyle = {
  display: 'flex',
  gap: 'var(--sp-2)',
  alignItems: 'center',
  minHeight: 'var(--field-h)',
} as const;
const alertStyle = {
  color: 'var(--danger)',
  background: 'var(--danger-weak)',
  borderRadius: 'var(--r-sm)',
  padding: 'var(--sp-2) var(--sp-3)',
} as const;
const actionsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-2)',
  justifyContent: 'flex-end',
} as const;
